import { TenantFeatureToggle, TenantType } from '@xeetr/types/enums'
import { roundToNearestMinutes, set } from 'date-fns'
import { BarChart3, CalendarPlus, CalendarX, Link, UserPlus, Zap } from 'lucide-react'

import { useSidebar } from '../../hooks/useSidebar'
import { CustomEvents } from '../../types/AnalyticsKeys'
import { ButtonDefinition } from '../../types/ButtonDefinition'
import { SidebarType } from '../../types/Sidebar'
import { calculateTimeEnd } from '../../utils/booking'
import useFeatureToggle from '../common/useFeatureToggle'
import { useDashboard } from '../useDashboard'
import { useTenant } from '../useTenant'

import { showToast } from '@/utils/toastMessage'
import { useRooms } from '../useRooms'

export const useToolbarButtons = (): ButtonDefinition[] => {
  const { tenant } = useTenant()
  const { rooms } = useRooms()
  const { showStatistics, setShowStatistics } = useDashboard()

  const { setSidebar } = useSidebar()

  const foodEventFeature = useFeatureToggle(TenantFeatureToggle.FOOD_EVENTS)
  const walkInFeature = useFeatureToggle(TenantFeatureToggle.WALKIN)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      showToast({ severity: 'success', detail: 'Link in Zwischenablage kopiert' })
    } catch (error) {
      console.error('Failed to copy link:', error)
      showToast({ severity: 'error', detail: 'Fehler beim Kopieren des Links' })
    }
  }

  return [
    {
      trackingkey: CustomEvents.OPEN_ABSENCE_MODAL,
      icon: CalendarX,
      tooltip: 'Abwesenheit eintragen',
      onClick: () =>
        setSidebar({
          open: true,
          type: SidebarType.ABSENCE,
        }),
      visible: tenant?.type === TenantType.SERVICE,
    },
    {
      trackingkey: CustomEvents.OPEN_FOOD_EVENT_MODAL,
      icon: CalendarPlus,
      tooltip: 'Neues Event erstellen',
      onClick: () =>
        setSidebar({
          open: true,
          type: SidebarType.EVENT,
        }),
      visible: foodEventFeature && tenant?.type === TenantType.FOOD && rooms && rooms.length > 0,
    },
    {
      trackingkey: CustomEvents.OPEN_FAST_CHECKIN_MODAL,
      icon: Zap,
      tooltip: 'Fast Check-In',
      onClick: () =>
        setSidebar({
          open: true,
          type: SidebarType.FAST_CHECKIN,
        }),
      visible: walkInFeature,
    },
    {
      trackingkey: CustomEvents.OPEN_BOOKING_MODAL,
      icon: UserPlus,
      tooltip: 'Neue Reservierung anlegen',
      onClick: () => {
        const now = roundToNearestMinutes(new Date(), { nearestTo: 30 })
        const start = set(new Date(), {
          hours: now.getHours(),
          minutes: now.getMinutes(),
          seconds: 0,
        })
        const end = tenant ? calculateTimeEnd(start, tenant) : start
        setSidebar({
          open: true,
          type: SidebarType.BOOKING_CREATE,
          options: { start, end },
        })
      },
    },
    {
      trackingkey: CustomEvents.TOGGLE_STATISTICS,
      icon: BarChart3,
      tooltip: showStatistics ? 'Statistiken ausblenden' : 'Statistiken anzeigen',
      onClick: () => setShowStatistics(!showStatistics),
      variant: showStatistics ? 'default' : 'outline',
      visible: tenant?.showStatisticsInDashboard ?? false,
    },
    {
      trackingkey: CustomEvents.COPY_DASHBOARD_LINK,
      icon: Link,
      tooltip: 'Link zur aktuellen Ansicht kopieren',
      onClick: handleCopyLink,
      visible: tenant?.showCopyLinkInDashboard ?? false,
    },
  ]
}
